<script setup lang="ts">
import {
  equalsMoney,
  formatLocalDate,
  parseLocalDate,
  signOfMoney,
  systemClock,
  todayUtc,
  type Money,
} from '@taler/domain'
import { ActionButton, Dialog, StatusTag } from '@taler/ui'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker, useEntryTargets } from '@/features/entries'
import { useContactPayback } from '../composables/use-contact-payback.ts'
import { defaultPaybackDirection, toPaybackEntry, type PaybackDirection } from '../internal/payback.ts'
import type { Contact } from '../internal/types.ts'

const props = defineProps<{
  open: boolean
  ledgerId: string | undefined
  contact: Contact
  balances: readonly Money[]
}>()

const emit = defineEmits<{ close: []; saved: [] }>()
const { locale, t } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const FIELD_CLASS =
  'min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] aria-[invalid=true]:border-[color:var(--color-danger)] disabled:opacity-70'
const LABEL_CLASS = 'text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]'
const BUTTON_CLASS =
  'min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)] disabled:opacity-60'

interface DraftState {
  readonly direction: PaybackDirection
  readonly moneyAccountId: string | undefined
  readonly amount: Money | null
  readonly date: string
  readonly memo: string
}

const direction = ref<PaybackDirection>('THEY_PAID_ME')
const directionTouched = ref(false)
const moneyAccountId = ref<string>()
const amount = ref<Money | null>(null)
const date = ref('')
const memo = ref('')
const submitAttempted = ref(false)
const pendingClose = ref(false)
const keepEditingButton = ref<HTMLButtonElement>()
const baseline = ref<DraftState>()

const targets = useEntryTargets({
  ledgerId: () => props.ledgerId,
  locale: () => currentLocale.value,
  enabled: () => props.open,
})
const selectedCurrency = computed(
  () => targets.moneyTargets.value.find((target) => target.id === moneyAccountId.value)?.currency,
)

const payback = useContactPayback({
  ledgerId: () => props.ledgerId,
  contactId: () => props.contact.id,
})

function draftState(): DraftState {
  return {
    direction: direction.value,
    moneyAccountId: moneyAccountId.value,
    amount: amount.value,
    date: date.value,
    memo: memo.value,
  }
}

function sameDraft(a: DraftState, b: DraftState): boolean {
  return (
    a.direction === b.direction &&
    a.moneyAccountId === b.moneyAccountId &&
    a.date === b.date &&
    a.memo === b.memo &&
    (a.amount === null || b.amount === null ? a.amount === b.amount : equalsMoney(a.amount, b.amount))
  )
}

const dirty = computed(() => baseline.value !== undefined && !sameDraft(baseline.value, draftState()))

function resetDraft(): void {
  direction.value = defaultPaybackDirection(props.balances)
  directionTouched.value = false
  moneyAccountId.value = undefined
  amount.value = null
  date.value = formatLocalDate(todayUtc(systemClock))
  memo.value = t('contacts.payback.memoDefault', { name: props.contact.name })
  submitAttempted.value = false
  pendingClose.value = false
  payback.reset()
  baseline.value = draftState()
}

watch(
  () => props.open,
  (open) => {
    if (open) resetDraft()
  },
  { immediate: true },
)

watch(pendingClose, async (pending) => {
  if (!pending) return
  await nextTick()
  keepEditingButton.value?.focus()
})

function warnBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault()
}

const unsavedDraft = computed(() => props.open && dirty.value)
watch(unsavedDraft, (unsaved) => {
  if (unsaved) window.addEventListener('beforeunload', warnBeforeUnload)
  else window.removeEventListener('beforeunload', warnBeforeUnload)
})
onBeforeUnmount(() => window.removeEventListener('beforeunload', warnBeforeUnload))

function chooseDirection(next: PaybackDirection): void {
  directionTouched.value = true
  direction.value = next
}

function chooseAccount(value: string | null): void {
  moneyAccountId.value = value ?? undefined
  if (directionTouched.value) return
  const currency = targets.moneyTargets.value.find((target) => target.id === value)?.currency
  direction.value = defaultPaybackDirection(props.balances, currency)
}

function requestClose(): void {
  if (payback.isSubmitting.value) return
  if (dirty.value) pendingClose.value = true
  else emit('close')
}

function discardChanges(): void {
  pendingClose.value = false
  emit('close')
}

const accountError = computed(() =>
  moneyAccountId.value === undefined ? t('contacts.payback.accountRequired') : undefined,
)
const amountError = computed(() =>
  amount.value === null || signOfMoney(amount.value) <= 0 ? t('contacts.payback.amountRequired') : undefined,
)
const dateError = computed(() => (date.value === '' ? t('contacts.payback.dateRequired') : undefined))
const memoError = computed(() => (memo.value.trim() === '' ? t('contacts.payback.memoRequired') : undefined))
const hasErrors = computed(
  () =>
    accountError.value !== undefined ||
    amountError.value !== undefined ||
    dateError.value !== undefined ||
    memoError.value !== undefined,
)

function visibleError(error: string | undefined): string | undefined {
  return submitAttempted.value ? error : undefined
}

async function submit(): Promise<void> {
  submitAttempted.value = true
  if (
    hasErrors.value ||
    moneyAccountId.value === undefined ||
    amount.value === null ||
    selectedCurrency.value === undefined
  ) {
    return
  }
  const input = toPaybackEntry({
    direction: direction.value,
    moneyAccountId: moneyAccountId.value,
    contactAccountId: props.contact.accountId,
    amount: amount.value,
    date: parseLocalDate(date.value),
    memo: memo.value,
  })
  try {
    await payback.submit(input)
    emit('saved')
  } catch {
    // The mutation state renders the server's error.
  }
}
</script>

<template>
  <Dialog :open="open" :label="t('contacts.payback.title')" @close="requestClose">
    <header
      class="flex items-start justify-between gap-[var(--space-cell)] px-[var(--space-block)] pt-[var(--space-block)]"
    >
      <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
        <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ contact.name }}
        </p>
        <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
          {{ t('contacts.payback.title') }}
        </h2>
      </div>
      <button type="button" :class="BUTTON_CLASS" @click="requestClose">
        {{ t('contacts.payback.close') }}
      </button>
    </header>

    <form novalidate class="p-[var(--space-block)]" @submit.prevent="submit">
      <fieldset :disabled="payback.isSubmitting.value" class="flex min-w-0 flex-col gap-[var(--space-block)]">
        <fieldset class="flex flex-col gap-[var(--space-inline)]">
          <legend :class="LABEL_CLASS" class="pb-[var(--space-hair)]">
            {{ t('contacts.payback.directionLabel') }}
          </legend>
          <div class="grid grid-cols-2 gap-[var(--space-inline)]">
            <label
              class="flex min-h-[var(--density-control)] cursor-pointer items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[length:var(--text-step-3)]"
            >
              <input
                type="radio"
                name="payback-direction"
                value="THEY_PAID_ME"
                :checked="direction === 'THEY_PAID_ME'"
                @change="chooseDirection('THEY_PAID_ME')"
              />
              {{ t('contacts.payback.theyPaidMe') }}
            </label>
            <label
              class="flex min-h-[var(--density-control)] cursor-pointer items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[length:var(--text-step-3)]"
            >
              <input
                type="radio"
                name="payback-direction"
                value="I_PAID_THEM"
                :checked="direction === 'I_PAID_THEM'"
                @change="chooseDirection('I_PAID_THEM')"
              />
              {{ t('contacts.payback.iPaidThem') }}
            </label>
          </div>
        </fieldset>

        <div class="flex flex-col gap-[var(--space-hair)]">
          <AccountPicker
            id="payback-money-account"
            :label="t('contacts.payback.moneyAccountLabel')"
            :model-value="moneyAccountId"
            filter-class="ASSET"
            :ledger-id="ledgerId"
            :disabled="targets.isLoading.value"
            @update:model-value="chooseAccount"
          />
          <p
            v-if="visibleError(accountError)"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
          >
            {{ accountError }}
          </p>
        </div>

        <div class="grid grid-cols-[minmax(0,1fr)_7rem] gap-[var(--space-inline)]">
          <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
            <label :class="LABEL_CLASS" for="payback-amount">{{ t('contacts.payback.amountLabel') }}</label>
            <AmountInput
              id="payback-amount"
              v-model="amount"
              :currency="selectedCurrency"
              :locale="currentLocale"
              required
              :aria-label="t('contacts.payback.amountLabel')"
              :aria-invalid="visibleError(amountError) !== undefined"
            />
            <p
              v-if="visibleError(amountError)"
              class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
            >
              {{ amountError }}
            </p>
          </div>
          <div class="flex flex-col gap-[var(--space-hair)]">
            <label :class="LABEL_CLASS" for="payback-currency">
              {{ t('contacts.payback.currencyLabel') }}
            </label>
            <input
              id="payback-currency"
              :value="selectedCurrency ?? ''"
              type="text"
              readonly
              :placeholder="t('contacts.payback.currencyPlaceholder')"
              :class="FIELD_CLASS"
            />
          </div>
        </div>
        <p
          class="-mt-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
        >
          {{ t('contacts.payback.currencyHint') }}
        </p>

        <div class="grid grid-cols-[10rem_minmax(0,1fr)] gap-[var(--space-group)]">
          <div class="flex flex-col gap-[var(--space-hair)]">
            <label :class="LABEL_CLASS" for="payback-date">{{ t('contacts.payback.dateLabel') }}</label>
            <input
              id="payback-date"
              v-model="date"
              type="date"
              required
              :aria-invalid="visibleError(dateError) !== undefined"
              :class="FIELD_CLASS"
            />
            <p
              v-if="visibleError(dateError)"
              class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
            >
              {{ dateError }}
            </p>
          </div>
          <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
            <label :class="LABEL_CLASS" for="payback-memo">{{ t('contacts.payback.memoLabel') }}</label>
            <input
              id="payback-memo"
              v-model="memo"
              type="text"
              required
              :aria-invalid="visibleError(memoError) !== undefined"
              :class="FIELD_CLASS"
            />
            <p
              v-if="visibleError(memoError)"
              class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
            >
              {{ memoError }}
            </p>
          </div>
        </div>

        <StatusTag v-if="payback.hasError.value" tone="danger">
          {{ payback.errorDetail.value ?? t('contacts.payback.saveError') }}
        </StatusTag>

        <div
          v-if="pendingClose"
          role="alertdialog"
          aria-labelledby="payback-discard-title"
          aria-describedby="payback-discard-consequence"
          class="sticky bottom-0 -mx-[var(--space-block)] -mb-[var(--space-block)] flex flex-col gap-[var(--space-inline)] border-t border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-block)] py-[var(--space-group)]"
        >
          <h3
            id="payback-discard-title"
            class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]"
          >
            {{ t('contacts.payback.discardTitle') }}
          </h3>
          <p
            id="payback-discard-consequence"
            class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ t('contacts.payback.discardConsequence') }}
          </p>
          <div class="flex justify-end gap-[var(--space-inline)]">
            <button ref="keepEditingButton" type="button" :class="BUTTON_CLASS" @click="pendingClose = false">
              {{ t('contacts.payback.keepEditing') }}
            </button>
            <button
              type="button"
              :class="[BUTTON_CLASS, 'text-[color:var(--color-danger)]']"
              @click="discardChanges"
            >
              {{ t('contacts.payback.discardAction') }}
            </button>
          </div>
        </div>
        <div
          v-else
          class="sticky bottom-0 -mx-[var(--space-block)] -mb-[var(--space-block)] flex justify-end gap-[var(--space-inline)] border-t border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-block)] py-[var(--space-group)]"
        >
          <button type="button" :class="BUTTON_CLASS" @click="requestClose">
            {{ t('contacts.payback.cancel') }}
          </button>
          <ActionButton type="submit" :disabled="payback.isSubmitting.value">
            {{ payback.isSubmitting.value ? t('contacts.payback.saving') : t('contacts.payback.save') }}
          </ActionButton>
        </div>
      </fieldset>
    </form>
  </Dialog>
</template>
